<template>
    <div class="max-w-6xl mx-auto pb-6 space-y-6">
        <h1 class="text-2xl font-bold text-gray-800">Painel</h1>

        <div v-if="loading" class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div v-for="i in 4" :key="i" class="bg-white border border-gray-100 shadow-sm rounded-xl p-4">
                <div class="skeleton h-3 w-20 rounded mb-2"></div>
                <div class="skeleton h-7 w-14 rounded"></div>
            </div>
        </div>

        <p v-else-if="errorMessage" class="text-sm text-red-600">{{ errorMessage }}</p>

        <template v-else>
            <!-- Metric cards -->
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <router-link :to="{ name: 'patients' }" class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 min-w-0 overflow-hidden hover:border-cyan-200 transition-colors">
                    <div class="text-xs text-gray-400 mb-1">Pacientes</div>
                    <div class="text-2xl font-bold text-gray-800">{{ summary.patients }}</div>
                </router-link>
                <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 min-w-0 overflow-hidden">
                    <div class="text-xs text-gray-400 mb-1">Avaliações</div>
                    <div class="text-2xl font-bold text-gray-800">{{ summary.evaluations }}</div>
                </div>
                <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 min-w-0 overflow-hidden">
                    <div class="text-xs text-gray-400 mb-1">Relatórios de IA</div>
                    <div class="text-2xl font-bold text-gray-800">{{ summary.reports }}</div>
                </div>
                <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-4 min-w-0 overflow-hidden">
                    <div class="text-xs text-gray-400 mb-1">Análises ACIA</div>
                    <div class="text-2xl font-bold text-gray-800">{{ summary.acias }}</div>
                </div>
            </div>

            <!-- Usage limits -->
            <section class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div class="flex items-center justify-between mb-4">
                    <h2 class="text-base font-semibold text-gray-500">Limites de uso este mês</h2>
                    <router-link :to="{ name: 'usageLimits' }" class="text-xs font-semibold text-cyan-600 hover:text-cyan-700">Ver detalhes</router-link>
                </div>
                <div class="space-y-4">
                    <UsageMeter
                        title="Relatórios com Inteligência Artificial"
                        :used="summary.usage.standard.used"
                        :limit="summary.usage.standard.limit"
                        :extra-credits="summary.usage.standard.extraCredits"
                    />
                    <UsageMeter
                        title="Análises ACIA"
                        :used="summary.usage.acia.used"
                        :limit="summary.usage.acia.limit"
                        :extra-credits="summary.usage.acia.extraCredits"
                    />
                </div>
            </section>

            <!-- Shortcuts -->
            <section>
                <h2 class="text-base font-semibold text-gray-500 mb-3">Atalhos</h2>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <router-link :to="{ name: 'patients' }" class="flex items-center gap-3 bg-white border border-gray-100 shadow-sm rounded-xl p-4 hover:border-cyan-200 transition-colors">
                        <span class="shrink-0 w-10 h-10 rounded-full bg-cyan-50 text-cyan-600 flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </span>
                        <div class="min-w-0">
                            <div class="text-sm font-semibold text-gray-800">Pacientes</div>
                            <div class="text-xs text-gray-400">Gerencie seus pacientes</div>
                        </div>
                    </router-link>
                    <router-link :to="{ name: 'users' }" class="flex items-center gap-3 bg-white border border-gray-100 shadow-sm rounded-xl p-4 hover:border-cyan-200 transition-colors">
                        <span class="shrink-0 w-10 h-10 rounded-full bg-cyan-50 text-cyan-600 flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        </span>
                        <div class="min-w-0">
                            <div class="text-sm font-semibold text-gray-800">Usuários</div>
                            <div class="text-xs text-gray-400">Gerencie quem tem acesso à sua conta</div>
                        </div>
                    </router-link>
                </div>
            </section>

            <section class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div class="flex items-center justify-between gap-3 mb-4">
                    <div>
                        <h2 class="text-base font-semibold text-gray-500">Últimas avaliações</h2>
                        <p class="text-xs text-gray-400 mt-1">Atalho rápido para abrir registros recentes.</p>
                    </div>
                    <router-link
                        :to="{ name: 'evaluations' }"
                        class="text-xs font-semibold text-cyan-600 bg-cyan-50 px-3 py-1 rounded-full hover:bg-cyan-100 transition-colors"
                    >
                        Ver avaliações
                    </router-link>
                </div>

                <div v-if="summary.recentEvaluations.length" class="space-y-3">
                    <component
                        v-for="record in summary.recentEvaluations"
                        :key="record.id"
                        :is="canOpenRecentRecord(record) ? 'router-link' : 'div'"
                        :to="canOpenRecentRecord(record) ? getRecentRecordRoute(record) : undefined"
                        :class="[
                            'block rounded-xl border border-gray-100 bg-white px-4 py-4 transition-all',
                            canOpenRecentRecord(record) ? 'hover:border-cyan-200 hover:shadow-sm' : 'opacity-75'
                        ]"
                    >
                        <div class="grid gap-4 md:grid-cols-[minmax(0,2.2fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_auto] md:items-center">
                            <div class="min-w-0">
                                <div class="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-600">Paciente</div>
                                <div class="mt-1 truncate text-sm font-semibold text-gray-800">{{ (record.patient && record.patient.name) || 'Paciente sem identificação' }}</div>
                            </div>
                            <div>
                                <div class="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">Criada em</div>
                                <div class="mt-1 text-sm text-gray-700">{{ defaultDate(record.created_at) }}</div>
                            </div>
                            <div>
                                <div class="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">Última atualização</div>
                                <div class="mt-1 text-sm text-gray-700">{{ dateTime(record.updated_at) }}</div>
                            </div>
                            <div class="flex items-center gap-2 text-sm font-semibold text-cyan-600">
                                <span>{{ canOpenRecentRecord(record) ? 'Abrir' : 'Indisponível' }}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                            </div>
                        </div>
                    </component>
                </div>

                <div v-else class="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                    Nenhuma avaliação recente encontrada.
                </div>
            </section>
        </template>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { DashboardService } from '@/services/Entity';
import UsageMeter from '@/components/UsageMeter.vue';
import { defaultDate, dateTime } from '@/filters/date';

export default {
    components: { UsageMeter },
    setup() {
        const loading = ref(true);
        const errorMessage = ref('');
        const summary = ref({
            patients: 0,
            evaluations: 0,
            reports: 0,
            acias: 0,
            usage: {
                standard: { used: 0, limit: 0, extraCredits: 0 },
                acia: { used: 0, limit: 0, extraCredits: 0 }
            },
            recentEvaluations: []
        });

        onMounted(async () => {
            try {
                const { data } = await DashboardService.getSummary();
                summary.value = (data && data.data) || summary.value;
            } catch (e) {
                errorMessage.value = 'Não foi possível carregar o painel. Tente novamente.';
            } finally {
                loading.value = false;
            }
        });

        function canOpenRecentRecord(record) {
            return Boolean(record && record.uuid && record.patient && record.patient.uuid);
        }

        function getRecentRecordRoute(record) {
            return {
                name: 'avaliation',
                params: {
                    patient_uuid: record.patient.uuid,
                    record_uuid: record.uuid
                }
            };
        }

        return { loading, errorMessage, summary, defaultDate, dateTime, canOpenRecentRecord, getRecentRecordRoute };
    }
};
</script>
